import { AfterViewInit, ApplicationRef, Component, EventEmitter, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { JobInformationFormComponent } from "./job-information-form/job-information-form.component";
import { ProductInformationFormComponent } from "./product-information-form/product-information-form.component";
import { NotificationFormComponent } from "./notification-form/notification-form.component";
import { FromLocationFormComponent } from "./from-location-form/from-location-form.component";
import { ToLocationFormComponent } from "./to-location-form/to-location-form.component";
import { JobbingAttachmentFormComponent } from "./jobbing-attachment-form/jobbing-attachment-form.component";
import { AsyncPipe, CommonModule } from '@angular/common';
import { forkJoin, Observable } from 'rxjs';
import { FreightPayer } from '../../../../../../shared/freight-payers/models/FreightPayer';
import { FreightPayersService } from '../../../../../../shared/freight-payers/freight-payers.service';
import { ActivatedRoute, Router } from '@angular/router';
import { JobbingService } from '../../../../../../shared/Jobbing/jobbing.service';
import { ToasterService } from '../../../../../../shared/toaster/toaster.service';
import dayjs from 'dayjs';
import { Job } from '../../../../../../shared/Jobbing/models/Job';
import { JobProduct } from '../../../../../../shared/Jobbing/models/JobProduct';
import { JobProductItem } from '../../../../../../shared/Jobbing/models/JobProductItem';
import { UpdateJobMetaDataModel } from '../../../../../../shared/Jobbing/models/job-update-models/UpdateJobMetaDataModel';
import { JobServicePromise } from '../../../../../../shared/Jobbing/models/JobServicePromise';
import { JobPriority } from '../../../../../../shared/Jobbing/models/JobPriority';
import { UpdateJobDeliveryInformation } from '../../../../../../shared/Jobbing/models/job-update-models/UpdateJobDeliveryInformationModel';
import { GoogleMapsService } from '../../../../../../shared/google-maps/google-maps.service';
import { UpsertAttachmentModel } from '../../../../../../shared/Jobbing/models/job-update-models/UpsertAttachmentModel';
import { FormDefaultsService } from '../../../../../../shared/form-defaults/form-defaults.service';
import { DefaultType } from '../../../../../../shared/form-defaults/models/DefaultType';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-jobbing-upsert-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    JobInformationFormComponent,
    ProductInformationFormComponent,
    NotificationFormComponent,
    FromLocationFormComponent,
    ToLocationFormComponent,
    JobbingAttachmentFormComponent,
    AsyncPipe,
    CommonModule
  ],
  templateUrl: './jobbing-upsert-form.component.html',
  styleUrl: './jobbing-upsert-form.component.scss'
})
export class JobbingUpsertFormComponent implements OnInit, AfterViewInit {

  @Input()
  jobId: number | null = null; // This will hold the order id from the route

  isEdit: boolean = false;
  formGroup!: FormGroup;
  disableEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  resetEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  productDefaultEmitter: EventEmitter<any> = new EventEmitter<{ freightPayer: number, isTimeBased: boolean, products: any[] }>();
  freightPayers$!: Observable<FreightPayer[]>;
  priorityOptions$!: Observable<any[]>;
  servicePromiseOptions$!: Observable<any[]>;
  orderCreatedSuccessMode: any;
  servicePromiseOptions!: JobServicePromise[] | null;
  priorityOptions!: JobPriority[] | null;
  startingProducts: any[] | null = null;
  originalJob: Job | null = null;
  isInitialized: boolean = false;
  startingDefaults: string = '';

  constructor(private readonly fb: FormBuilder, private readonly freightPayerService: FreightPayersService, private readonly route: ActivatedRoute, private readonly jobbingService: JobbingService, private readonly toasterService: ToasterService, private readonly router: Router, private readonly googleMapsService: GoogleMapsService, private readonly formDefaultService: FormDefaultsService,
    private readonly appRef: ApplicationRef
  ) { }

  ngAfterViewInit(): void {
    this.startingDefaults = JSON.stringify(this.formGroup.value);
    this.formGroup.get('jobInformation')!.get('freightPayer')!.valueChanges.subscribe((value) => {
      this.isInitialized = false;
      if (value && !this.isEdit) {
        this.formDefaultService.getFormDefaults(value, DefaultType.Jobbing).subscribe((response: any) => {
          if (response && response.formValueJson !== "") {
            const formValue = JSON.parse(response.formValueJson);
            const productList = formValue.productInformation.products.map((product: any) => {
              return {
                productCategory: { ...product.category },
                productItems: product.items.map((item: any) => {
                  return {
                    jobProductItemId: item.jobProductItemId,
                    itemDescription: item.description,
                    quantity: item.quantity,
                    weight: item.weight,
                    volume: item.volumeM3,
                    dangerousGoodsClass: item.dangerousGoodsClass
                  }
                })
              }
            });

            this.appRef.tick();

            this.formGroup.patchValue({
              ...formValue,
              productInformation: null
            }, { emitEvent: false });

            this.formGroup.patchValue({
              jobFrom: {
                ...formValue.jobFrom
              },
              jobTo: {
                ...formValue.jobTo
              }
            });

            this.formGroup.patchValue({
              jobInformation: {
                loadingServicePromise: formValue.jobInformation.loadingServicePromise,
                deliveryServicePromise: formValue.jobInformation.deliveryServicePromise,
                lspTime: dayjs(formValue.jobInformation.lspTime).toDate(),
                dspTime: dayjs(formValue.jobInformation.dspTime).toDate(),
                estimatedHours: formValue.jobInformation.estimatedHours
              }
            });
            this.productDefaultEmitter.emit({ freightPayer: value, isTimeBased: this.isTimeBased(), products: productList });

          } else {
            const startValue = JSON.parse(this.startingDefaults);
            this.startingProducts = null;
            this.formGroup.patchValue({
              ...startValue,
              jobInformation: {
                ...startValue.jobInformation,
                freightPayer: value
              }
            }, { emitEvent: false });
            this.formGroup.patchValue({
              jobInformation: {
                loadingServicePromise: startValue.jobInformation.loadingServicePromise,
                deliveryServicePromise: startValue.jobInformation.deliveryServicePromise,
                estimatedHours: startValue.jobInformation.estimatedHours
              }
            });
            this.productDefaultEmitter.emit({ freightPayer: value, isTimeBased: this.isTimeBased(), products: null });
          }
          this.isInitialized = true;

        });
      }
    });
  }

  ngOnInit() {
    this.freightPayers$ = this.freightPayerService.search();
    this.priorityOptions$ = this.jobbingService.getJobPriorities();
    this.servicePromiseOptions$ = this.jobbingService.getServicePromises();

    this.servicePromiseOptions$.subscribe(value => {
      this.servicePromiseOptions = value
    });
    this.priorityOptions$.subscribe(value => {
      this.priorityOptions = value
    });

    this.formGroup = this.fb.group({
    });

    if (this.jobId !== null) {
      this.setFormForEdit();
    } else {
      this.formGroup = this.fb.group({
      });
      this.isInitialized = true;
    }
  }

   setFormForEdit() {
    this.jobbingService.getJobDetails(this.jobId!).subscribe((job: any) => {
      this.isEdit = true;
      this.originalJob = job;
      const jobPriority = job.movements.length > 0 ? job.movements[0].jobPriority.jobPriorityId : null;
      const requiredDate = dayjs(job.requiredDate);
      const [lspHour, lspMinute, lspSecond] = job.jobLspTime ? [...(job.jobLspTime as string).split(':')] : dayjs().format('HH:mm:ss').split(':');
      const [dspHour, dspMinute, dspSecond] = job.jobDspTime ? [...(job.jobDspTime as string).split(':')] : dayjs().format('HH:mm:ss').split(':');
      const lspTime = dayjs().set('hour', parseInt(lspHour)).set('minute', parseInt(lspMinute)).set('second', parseInt(lspSecond)).toDate();
      const dspTime = dayjs().set('hour', parseInt(dspHour)).set('minute', parseInt(dspMinute)).set('second', parseInt(dspSecond)).toDate();
      this.startingProducts = job.jobProducts;

      this.appRef.tick();
      this.formGroup.patchValue({
        jobInformation: {
          freightPayer: job.freightPayer?.freightPayerId,
          requiredCompletionDate: {
            year: requiredDate.year(),
            month: requiredDate.month() + 1,
            day: requiredDate.date()
          },
          deliveryServicePromise: job.jobDsp.servicePromiseId,
          loadingServicePromise: job.jobLsp.servicePromiseId,
          orderNumber: job.orderNumber,
          timeBased: job.isTimeBased,
          jobReference: job.jobReference,
          priority: jobPriority,
          jobDescription: job.jobDescription,
          lspTime: lspTime,
          dspTime: dspTime,
        },
        notification: {
          email: {
            name: job.notification?.emailName,
            address: job.notification?.email
          },
          mobile: {
            name: job.notification?.mobileName,
            number: job.notification?.mobileNumber
          }
        },
        attachments: {
          documents: [...job.attachments]
        },
      }, { emitEvent: false });

      this.appRef.tick();

      this.formGroup.patchValue({
        jobFrom: {
          onSite: job.onSite,
          contact: {
            ...job.fromJobContact
          },
          addressSelection: {
            addressName: job.fromJobAddressSelection?.name,
            masterContactAddressId: {
              masterContactId: job.fromJobAddressSelection?.masterContactId,
              masterContactName: job.fromJobAddressSelection?.name,
            }
          }
        },
        jobTo: {
          contact: {
            ...job.toJobContact
          },
          addressSelection: {
            addressName: job.toJobAddressSelection?.name,
            masterContactAddressId: {
              masterContactId: job.toJobAddressSelection?.masterContactId,
              masterContactName: job.toJobAddressSelection?.name,
            }
          }
        }
      });
      this.appRef.tick();

      this.formGroup.patchValue({
        jobFrom: {
          preciseLocationOptions: {
            deliveryLocationOverrideId: job.fromDeliveryLocationOverride?.deliveryLocationOverrideId,
          }
        },
        jobTo: {
          preciseLocationOptions: {
            deliveryLocationOverrideId: job.toDeliveryLocationOverride?.deliveryLocationOverrideId,
          }
        }
      });
      this.appRef.tick();

      this.formGroup.get('jobInformation.freightPayer')?.updateValueAndValidity();
      this.productDefaultEmitter.emit({ freightPayer: job.freightPayer?.freightPayerId, isTimeBased: job.isTimeBased, products: job.jobProducts });

      this.isInitialized = true;

      this.formGroup.patchValue({ jobInformation: { estimatedHours: job.estimatedHours } });
    });
  }

  clearForm() {
    this.formGroup.reset();
    this.resetEmitter.emit(true);
  }

  saveDefaults() {
    const jobInformationValues = this.formGroup.get('jobInformation')?.value;

    if (jobInformationValues?.freightPayer && jobInformationValues.freightPayer > 0) {
      this.formDefaultService.saveFormDefaults({
        clienteleId: jobInformationValues.freightPayer,
        formValueJson: JSON.stringify(this.formGroup.value),
        type: DefaultType.Jobbing,
        userId: ''
      }).subscribe((response: any) => {
        this.toasterService.showDefault('Defaults saved successfully');
      });
    }
  }

  clearDefaults() {
    const jobInformationValues = this.formGroup.get('jobInformation')?.value;

    if (jobInformationValues?.freightPayer && jobInformationValues.freightPayer > 0) {
      this.formDefaultService.deleteFormDefaults(jobInformationValues.freightPayer, DefaultType.Jobbing).subscribe((response: any) => {
        this.toasterService.showDefault('Defaults cleared successfully');
      });
    }
  }

  clearAndCreate() {
    throw new Error('Method not implemented.');
  }

  isTimeBased() {
    return this.formGroup.get('jobInformation')?.get('timeBased')?.value;
  }

  productsValid() {
    const products = this.formGroup.get('productInformation')?.get('products')?.value;
    if (!products || products.length === 0) {
      this.toasterService.showDanger('Please add at least one product');
      return false;
    }

    for (const product of products) {
      for (const item of product.items) {
        if (item.description.length > 100) {
          this.toasterService.showDanger('Item description cannot exceed 100 characters');
          return false;
        }

        if (item.quantity <= 0 && item.weight <= 0 && item.volumeM3 <= 0) {
          this.toasterService.showDanger('Please provide at least one of the following for each item: quantity, weight, volume');
          return false;
        }

        if (item.weight < 0.001 && item.weight !== 0) {
          this.toasterService.showDanger('Smallest increment for weight is 0.001');
          return false;
        }

        if (item.volumeM3 < 0.01 && item.volumeM3 !== 0) {
          this.toasterService.showDanger('Smallest increment for volume is 0.01');
          return false;
        }
      }
    }
    return true;
  }

  submit() {
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();

    if (!this.productsValid() || !this.validateFromToAddress()) {
      return;
    }

    if (this.isEdit) {
      if (this.originalJob) {
        this.updateJob();
      } else {
        this.toasterService.showDanger('Error updating job: The original job was not found.');
      }
    } else {
      this.formGroup.markAllAsTouched();
      if (this.formGroup.valid) {
        let products: any[] = [];
        products = this.formGroup.get('productInformation')?.get('products')?.value;

        products = products.map((product: any) => {
          return {
            ...product,
            productItems: product.items.map((item: any) => {
              let weight, volume, quantity = 0;

              if (item.weight !== null && item.weight !== "") {
                weight = item.weight;
              }

              if (item.volumeM3 !== null && item.volumeM3 !== "") {
                volume = item.volumeM3;
              }

              if (item.quantity !== null && item.quantity !== "") {
                quantity = item.quantity;
              }

              return {
                ...item,
                itemDescription: item.description,
                weight: weight ?? 0,
                volume: volume ?? 0,
                quantity: quantity ?? 0,
              }
            }),
            productCategory: product.category,
            jobProductId: 0,
          };
        });

        if (this.isTimeBased()) {
          products = products.map((product: any) => {
            return {
              ...product,
              productItems: product.items.map((item: any) => {
                let weight, volume, quantity = 0;

                if (item.weight !== null && item.weight !== "") {
                  weight = item.weight;
                }

                if (item.volumeM3 !== null && item.volumeM3 !== "") {
                  volume = item.volumeM3;
                }

                if (item.quantity !== null && item.quantity !== "") {
                  quantity = item.quantity;
                }

                return {
                  ...item,
                  itemDescription: item.description,
                  weight: weight ?? 0,
                  volume: volume ?? 0,
                  quantity: quantity ?? 0,
                }
              }),
              productCategory: products[0].category,
              jobProductId: 0,
            };
          });
        }

        const documents = this.formGroup.get('attachments')?.get('documents')?.value;
        const model = {
          ...this.formGroup.value,
          jobInformation: {
            ...this.formGroup.value.jobInformation,
            lspTime: {
              hour: dayjs(this.formGroup.value.jobInformation.lspTime).get('hour'),
              minute: dayjs(this.formGroup.value.jobInformation.lspTime).get('minute'),
              second: dayjs(this.formGroup.value.jobInformation.lspTime).get('second'),
            },
            dspTime: {
              hour: dayjs(this.formGroup.value.jobInformation.dspTime).get('hour'),
              minute: dayjs(this.formGroup.value.jobInformation.dspTime).get('minute'),
              second: dayjs(this.formGroup.value.jobInformation.dspTime).get('second'),
            }
          },
          productInformation: {
            products: products
          }
        }

        this.jobbingService.saveJobbingJob(model, documents).subscribe({
          next: (response: any) => {
            this.toasterService.showDefault('Job created successfully');
            this.router.navigate(['app/jobbing/home']);
          },
          error: (error: HttpErrorResponse) => {
            this.toasterService.showDanger('Error creating job');
          }
        });
      } else {
        this.toasterService.showDanger('Please fill in all required fields');
      }
    }
  }

  isOnSite() {
    const jobFrom = this.formGroup.get('jobFrom');
    if (jobFrom) {
      return jobFrom.get('onSite')?.value;
    }
  }

  async updateJob() {
    const jobInformationValues = this.formGroup.get('jobInformation')?.value;
    const jobFromValues = this.formGroup.get('jobFrom')?.value;
    const jobToValues = this.formGroup.get('jobTo')?.value;
    const attachmentValues = this.formGroup.get('attachments')?.value;
    const notificationValues = this.formGroup.get('notification')?.value;
    const productInformationValues = this.formGroup.get('productInformation')?.value;
    const jobProducts = this.getJobProducts();

    if (!this.jobId ||
      !this.originalJob ||
      !jobInformationValues ||
      !jobFromValues ||
      !jobToValues ||
      !attachmentValues ||
      !notificationValues ||
      !productInformationValues) {
      this.toasterService.showDanger("Error updating job: Portions of the job form have failed to initialize.");
    }

    if (this.formGroup.valid) {
      const observableList: Observable<any>[] = [];
      if (this.getFormKeyValue(this.formGroup.get('jobInformation'), 'freightPayer', 'dirty')) {
        const changeJobFreightPayermodel: any =
        {
          freightPayer: {
            freightPayerId: jobInformationValues.freightPayer,
          },
          jobProducts: jobProducts,
          jobManualBilling: null, //this.chargeRateTypeChange(),
          jobSurcharges: [] //this.getJobSurcharges()
        }
        observableList.push(this.jobbingService.updateJobFreightPayer(this.jobId!, changeJobFreightPayermodel));
      }

      if (!this.getFormKeyValue(this.formGroup, 'jobInformation', 'dirty')
        && this.getFormKeyValue(this.formGroup.get('productInformation'), 'products', 'dirty')) {
        observableList.push(this.jobbingService.updateJobMetrics(this.jobId!, {
          jobProducts
        }
        ));
      }

      if (this.getFormKeyValue(this.formGroup, 'jobInformation', 'dirty')
        && !this.getFormKeyValue(this.formGroup.get('jobInformation'), 'freightPayer', 'dirty')
        && this.getFormKeyValue(this.formGroup.get('productInformation'), 'products', 'dirty')) {
        const changeTimeBasedModel = {
          isTimeBased: jobInformationValues.timeBased,
          jobProducts: jobProducts
        }

        observableList.push(this.jobbingService.updateJobTimeBased(this.jobId!, changeTimeBasedModel));
      }

      if (this.getFormKeyValue(this.formGroup, 'jobInformation', 'dirty')) {

        const dspItem = this.servicePromiseOptions?.find((servicePromise) => servicePromise.servicePromiseId === jobInformationValues.deliveryServicePromise);
        const lspItem = this.servicePromiseOptions?.find((servicePromise) => servicePromise.servicePromiseId === jobInformationValues.loadingServicePromise);
        const priorityItem = this.priorityOptions?.find((priority) => priority.jobPriorityId === jobInformationValues.priority);

        const changeMetaDataModel: UpdateJobMetaDataModel = {
          division: this.originalJob!.division,
          driverNotes: this.originalJob!.driversNotes,
          isTimeBased: jobInformationValues.timeBased,
          jobDescription: jobInformationValues.jobDescription,
          jobComments: this.originalJob!.jobComments,
          jobReference: jobInformationValues.jobReference,
          orderNumber: jobInformationValues.orderNumber,
          partialBilling: this.originalJob!.partialBilling,
          jobDsp: dspItem ?? {
            name: "",
            servicePromiseId: jobInformationValues.deliveryServicePromise,
          },
          jobDsptime: {
            hour: dayjs(jobInformationValues.dspTime).get('hour'),
            minute: dayjs(jobInformationValues.dspTime).get('minute'),
            second: dayjs(jobInformationValues.dspTime).get('second'),
          },
          jobEquipment: this.originalJob?.jobEquipments!,
          jobLsp: lspItem ?? {
            name: "",
            servicePromiseId: jobInformationValues.loadingServicePromise,
          },
          jobLsptime: {
            hour: dayjs(jobInformationValues.lspTime).get('hour'),
            minute: dayjs(jobInformationValues.lspTime).get('minute'),
            second: dayjs(jobInformationValues.lspTime).get('second'),
          },
          jobPriority: priorityItem ?? {
            jobPriorityId: jobInformationValues.priority,
            name: "",
            class: "",
            colour: ""
          },
          requiredDate: jobInformationValues.requiredCompletionDate,
          timeEstimate: jobInformationValues.estimatedHours
        }

        observableList.push(this.jobbingService.updateJobMetaData(this.jobId!, changeMetaDataModel));
      }

      if (this.getFormKeyValue(this.formGroup, 'jobFrom', 'dirty')
        || this.getFormKeyValue(this.formGroup, 'jobTo', 'dirty')
        || this.getFormKeyValue(this.formGroup, 'notification', 'dirty')) {

        let fromJobAddressValue = null;
        let toJobAddressValue = null;

        if (!jobFromValues.addressSelection) {
          this.toasterService.showDanger('From address is required');
        } else {
          if (jobFromValues.addressSelection.isCreateMode) {
            fromJobAddressValue = {
              name: jobFromValues.addressSelection.addressName,
              address: this.googleMapsService.getAddressLineFromAddressComponents(jobFromValues.addressSelection.addressDetails.addressComponents),
              latitude: jobFromValues.addressSelection.addressDetails.lat,
              longitude: jobFromValues.addressSelection.addressDetails.lng,
              masterContactId: 0,
            }
          } else {
            fromJobAddressValue = {
              name: jobFromValues.addressSelection.masterContactAddressId.masterContactName,
              masterContactId: jobFromValues.addressSelection.masterContactAddressId?.masterContactId,
              address: "",
              latitude: 0,
              longitude: 0
            }
          }
        }

        if (!jobFromValues.onSite && !jobToValues.addressSelection) {
          this.toasterService.showDanger('From address is required');
        } else {
          if (jobToValues.addressSelection.isCreateMode) {
            toJobAddressValue = !jobFromValues.onSite ? {
              name: jobToValues.addressSelection.addressName,
              address: this.googleMapsService.getAddressLineFromAddressComponents(jobToValues.addressSelection.addressDetails.addressComponents),
              latitude: jobToValues.addressSelection.addressDetails.lat,
              longitude: jobToValues.addressSelection.addressDetails.lng,
              masterContactId: 0,
            } : null
          } else {
            toJobAddressValue = !jobFromValues.onSite ? {
              name: jobToValues.addressSelection.masterContactAddressId.masterContactName,
              masterContactId: jobToValues.addressSelection.masterContactAddressId?.masterContactId,
              address: "",
              latitude: 0,
              longitude: 0
            } : null
          }
        }

        const changeJobDeliveryInformation: UpdateJobDeliveryInformation = {
          notification: {
            email: notificationValues.email.address,
            emailName: notificationValues.email.name,
            mobileNumber: notificationValues.mobile.number,
            mobileName: notificationValues.mobile.name
          },
          fromContact: {
            ...jobFromValues.contact,
            jobContactId: this.originalJob?.fromJobContact?.jobContactId ?? 0
          },
          toContact: {
            ...jobToValues.contact,
            jobContactId: this.originalJob?.toJobContact?.jobContactId ?? 0
          },
          fromJobAddressSelection: fromJobAddressValue,
          toJobAddressSelection: toJobAddressValue,
          fromDeliveryLocationOverride: {
            deliveryLocationOverrideId: jobFromValues.preciseLocationOptions?.deliveryLocationOverrideId
          },
          toDeliveryLocationOverride: {
            deliveryLocationOverrideId: jobToValues.preciseLocationOptions?.deliveryLocationOverrideId
          },
          onSite: jobFromValues.onSite,
          driversNotes: this.originalJob!.driversNotes,
          fromLocation: this.originalJob!.fromLocation,
          toLocation: this.originalJob!.fromLocation
        }

        observableList.push(this.jobbingService.updateJobDeliveryInformation(this.jobId!, changeJobDeliveryInformation));
      }

      if (this.getFormKeyValue(this.formGroup, 'attachments', 'dirty')) {

        const attachmentModel: UpsertAttachmentModel = {
          entityId: this.jobId!,
          workOrderType: "",
          // number 2 is from AttachmentEntityType in cds.ui
          entityType: 2,
          attachmentsToAdd: [],
          attachmentIdsToDelete: []
        }

        const originalAttachments = this.originalJob?.attachments || [];

        const attachmentsToRemove = originalAttachments.filter((item: any) => !attachmentValues.documents
          .some((originalItem: any) => originalItem.attachmentId === item.attachmentId))
          .map((item: any) => item.attachmentId);

        observableList.push(this.jobbingService.upsertJobAttachments(this.jobId!, {
          ...attachmentModel,
          attachmentIdsToDelete: attachmentsToRemove,
          attachmentsToAdd: []
        }, attachmentValues.documents));
      }

      forkJoin(observableList).subscribe(
        (result) => {
          this.toasterService.showDefault('Job updated successfully');
          this.router.navigate(['app/jobbing/home']);
        });
    } else {
      if (jobFromValues.addressSelection?.isCreateMode && jobFromValues.addressSelection.masterContactAddressId) {
        if (!jobFromValues.addressSelection.masterContactAddressId.masterContactName ||
          jobFromValues.addressSelection.masterContactAddressId.masterContactName === '') {
          this.toasterService.showDanger('Master contact name is required');
        }
        if (!jobFromValues.addressSelection.masterContactAddressId.masterContactId) {
          this.toasterService.showDanger('From address is required');
        }
      }

      if (jobFromValues.addressSelection && !jobFromValues.addressSelection.isCreateMode) {
        if (!jobFromValues.addressSelection.addressDetails && (!jobFromValues.addressSelection.addressName || jobFromValues.addressSelection.addressName === '')) {
          this.toasterService.showDanger('Please select an address');
        }
      }
      if (!jobFromValues.contact.email) {
        if (this.formGroup.get('jobInformation')?.get('contact')?.get('email')?.invalid) {
          this.toasterService.showDanger('The from address contacts email is malformed');
        }
      }
    }
  }

  validateFromToAddress() {
    let valid = true;

    const fromAddress = this.formGroup.get('jobFrom')?.value;
    const toAddress = this.formGroup.get('jobTo')?.value;

    if ((fromAddress.onSite && (!fromAddress || !fromAddress.addressSelection)) || (!fromAddress.onSite && (!fromAddress || !fromAddress.addressSelection || !toAddress || !toAddress.addressSelection))) {
      this.toasterService.show({ text: 'Please provide both collection and delivery addresses', delay: 5000, classname: 'bg-danger text-light' });
      return false;
    }

    if (fromAddress.addressSelection?.isCreateMode) {
      if (fromAddress.addressSelection?.addressDetails === null ||
        fromAddress.addressSelection?.addressDetails.addressComponents === null ||
        fromAddress.addressSelection?.addressDetails.addressComponents.length === 0 ||
        !fromAddress.addressSelection?.addressName || fromAddress.addressSelection?.addressName.trim() === ""
      ) {
        this.toasterService.show({ text: 'Please provide a collection address', delay: 5000, classname: 'bg-danger text-light' });
        valid = false;
      }
    } else {
      if (!fromAddress.addressSelection?.masterContactAddressId?.masterContactId) {
        this.toasterService.show({ text: 'Please select a collection address', delay: 5000, classname: 'bg-danger text-light' });
        valid = false;
      }
    }

    if (fromAddress.onSite){
      return valid;
    }

    if (toAddress.addressSelection?.isCreateMode) {
      if (toAddress.addressSelection?.addressDetails === null ||
        toAddress.addressSelection?.addressDetails.addressComponents === null ||
        toAddress.addressSelection?.addressDetails.addressComponents.length === 0 ||
        !toAddress.addressSelection?.addressName || toAddress.addressSelection?.addressName.trim() === ""
      ) {
        this.toasterService.show({ text: 'Please provide a delivery address', delay: 5000, classname: 'bg-danger text-light' });
        valid = false;
      }

    } else {
      if (!toAddress.addressSelection?.masterContactAddressId?.masterContactId ) {
        this.toasterService.show({ text: 'Please select a delivery address', delay: 5000, classname: 'bg-danger text-light' });
        valid = false;
      }
    }

    return valid;
  }

  getJobProducts() {
    const jobProductsAndItems: JobProduct[] = [];
    const formValue = this.formGroup.value;
    const products = formValue.productInformation?.products;
    products.forEach((product: any) => {
      jobProductsAndItems.push({
        jobProductId: product.jobProductId,
        productCategory: {
          productCategoryId: product.category.productCategoryId,
          ...product.category
        },
        productDescription: null,
        quantity: 0,
        originalQuantity: 0,
        unallocatedQuantity: 0,
        jobProductBilling: null,
        productItems: product.items ? this.getJobProductItems(product.items) : null,
        contractProductId: null, // maybe set?
        dispatchQuantity: 0,
        originalDispatchQuantity: 0,
        unallocatedDispatchQuantity: 0,
      })
    });
    return jobProductsAndItems;
  }

  getJobProductItems(productItems: any[]) {
    const jobProductsItems: JobProductItem[] = [];

    productItems.forEach((item: any) => {
      item.jobProductItemId = item.jobProductItemId;
      item.itemDescription = item.description;
      item.quantity = item.quantity ?? 0;
      item.weight = item.weight ?? 0;
      item.volume = item.volumeM3 ?? 0;
      item.dangerousGoodsClass = item.dangerousGoodsClass ?? null;

      jobProductsItems.push({
        ...item,
      });
    });
    return jobProductsItems;
  }

  getFormKeyValue(formGroup: any, formControl: any, formProperty: keyof FormGroup): boolean {
    const formValue = formGroup.get(formControl);

    if (formValue) {
      return formValue[formProperty];
    }
    return false;
  }
}
